
// =========================================================
// LA ESQUINA DEL PARQUÉS
// TORNEOS · EVENTOS · NOTICIAS · COMUNIDAD
// Blog.jsx
// =========================================================

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Trophy,
  CalendarDays,
  Search,
  Newspaper,
  Flame,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Clock3,
  UserRound,
  Sparkles,
  Gamepad2,
  Bell,
  RefreshCw,
  LoaderCircle,
  X,
} from "lucide-react";

import "./blog.css";
import blogBg from "../../assets/bg.png";
import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

// =========================================================
// API CONFIG
// =========================================================

const API_URL = (
  import.meta.env.VITE_API_URL ||
  "http://localhost:8000/api"
).replace(/\/+$/, "");

function getApiUrl(path = "") {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_URL}${cleanPath}`;
}

function getBackendOrigin() {
  return API_URL.replace(/\/api\/?$/, "").replace(/\/+$/, "");
}

// =========================================================
// UTILIDADES
// =========================================================

function getImageUrl(image) {
  if (!image || typeof image !== "string" || !image.trim()) {
    return blogBg;
  }

  const value = image.trim();

  if (/^(https?:\/\/|data:|blob:)/i.test(value)) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${getBackendOrigin()}${value}`;
  }

  return `${getBackendOrigin()}/${value.replace(/^\/+/, "")}`;
}

function formatDate(date) {
  if (!date) return "";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(date);
  }

  return parsedDate.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getCategoryName(article) {
  if (!article) return "COMUNIDAD";

  if (article.category_name) return article.category_name;

  if (article.category && typeof article.category === "object") {
    return article.category.name || "COMUNIDAD";
  }

  if (typeof article.category === "string") {
    return article.category || "COMUNIDAD";
  }

  return "COMUNIDAD";
}

function getCategorySlug(article) {
  if (article?.category_slug) return article.category_slug;

  if (article?.category && typeof article.category === "object") {
    return article.category.slug || "";
  }

  return "";
}

function getAuthorName(article) {
  if (!article) return "";

  if (article.author_name) return article.author_name;

  if (article.author && typeof article.author === "object") {
    return article.author.name || article.author.username || "";
  }

  if (typeof article.author === "string") {
    return article.author;
  }

  return "";
}

function getReadingTime(article) {
  if (article?.reading_time) {
    return `${article.reading_time} min de lectura`;
  }

  if (article?.read_time) return article.read_time;
  if (article?.readTime) return article.readTime;

  return "";
}

function normalizeArticle(article) {
  if (!article) return null;

  return {
    ...article,
    image: getImageUrl(
      article.cover_image ||
      article.image ||
      article.cover ||
      ""
    ),
    categoryName: getCategoryName(article),
    categorySlug: getCategorySlug(article),
    date: formatDate(article.published_at || article.created_at),
    authorName: getAuthorName(article),
    readTime: getReadingTime(article),
    excerpt: article.excerpt || article.summary || "",
  };
}

function getResults(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.articles)) return data.articles;
  if (Array.isArray(data?.items)) return data.items;

  return [];
}

// =========================================================
// TARJETA DE INFORMACIÓN
// =========================================================

function InfoCard({ icon: Icon, eyebrow, title, text, link, linkText }) {
  return (
    <article className="blog-info-card">
      <div className="blog-info-icon">
        <Icon size={24} />
      </div>

      <span className="blog-info-eyebrow">{eyebrow}</span>
      <h3>{title}</h3>
      <p>{text}</p>

      {link && (
        <Link to={link} className="blog-info-link">
          {linkText || "Descubrir más"}
          <ArrowRight size={16} />
        </Link>
      )}
    </article>
  );
}

// =========================================================
// BARRA LATERAL
// =========================================================

function Sidebar({
  categories,
  popular,
  search,
  setSearch,
  pageSettings,
  categoryFilter,
}) {
  function handleSearch(event) {
    event.preventDefault();

    const params = new URLSearchParams();

    if (search.trim()) {
      params.set("search", search.trim());
    }

    if (categoryFilter) {
      params.set("category", categoryFilter);
    }

    window.location.href = params.toString()
      ? `/blog?${params.toString()}`
      : "/blog";
  }

  const categoriesTitle =
    pageSettings?.categories_title || "EXPLORA LOS TEMAS";

  const popularTitle =
    pageSettings?.popular_title || "LO MÁS COMENTADO";

  const subscribeTitle =
    pageSettings?.subscribe_title || "NO TE PIERDAS NADA";

  const subscribeText =
    pageSettings?.subscribe_text ||
    "Entérate de los próximos torneos, actividades, novedades y eventos de La Esquina del Parqués.";

  return (
    <aside className="blog-sidebar">
      {/* BÚSQUEDA */}

      <form className="sidebar-search" onSubmit={handleSearch}>
        <Search size={19} />

        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={
            pageSettings?.search_placeholder ||
            "Buscar torneos y noticias..."
          }
          aria-label="Buscar torneos y noticias"
        />

        {search && (
          <button
            type="button"
            className="blog-search-clear"
            onClick={() => setSearch("")}
            aria-label="Limpiar búsqueda"
          >
            <X size={15} />
          </button>
        )}
      </form>

      {/* CATEGORÍAS */}

      <section className="sidebar-box">
        <h3>
          <Gamepad2 size={18} />
          {categoriesTitle}
        </h3>

        {categories.length > 0 ? (
          categories.map((category) => {
            const slug = category.slug || category.id;

            return (
              <Link
                key={category.id || category.slug}
                to={`/blog?category=${encodeURIComponent(slug)}`}
                className={
                  categoryFilter === slug ? "active" : ""
                }
              >
                <span>{category.name}</span>
                <b>{Number(category.article_count || 0)}</b>
              </Link>
            );
          })
        ) : (
          <p className="blog-sidebar-empty">
            Próximamente encontrarás aquí más temas.
          </p>
        )}
      </section>

      {/* ARTÍCULOS POPULARES */}

      <section className="sidebar-box">
        <h3>
          <Flame size={18} />
          {popularTitle}
        </h3>

        {popular.length > 0 ? (
          popular.map((post) => (
            <Link
              className="popular-post"
              to={`/blog/${post.slug}`}
              key={post.slug || post.id}
            >
              <img
                src={post.image || blogBg}
                alt={post.title || "Información de La Esquina"}
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.src = blogBg;
                }}
              />

              <span>
                <strong>{post.title}</strong>
                <small>{post.date}</small>
              </span>
            </Link>
          ))
        ) : (
          <p className="blog-sidebar-empty">
            Aún no hay publicaciones destacadas.
          </p>
        )}
      </section>

      {/* COMUNIDAD */}

      <section className="subscribe-box">
        <div className="blog-subscribe-icon">
          <Bell size={25} />
        </div>

        <h3>{subscribeTitle}</h3>
        <p>{subscribeText}</p>

        <Link to="/sedes" className="blog-subscribe-button">
          CONOCE LA ESQUINA
          <ArrowRight size={16} />
        </Link>
      </section>

      {/* PROMOCIÓN */}

      <div
        className="sidebar-promo"
        style={{
          backgroundImage: `url("${getImageUrl(
            pageSettings?.promo_image
          )}")`,
        }}
      >
        <span className="sidebar-promo-icon">
          <Trophy size={32} />
        </span>

        <strong>
          {pageSettings?.promo_text || "AQUÍ SE VIVEN GRANDES PARTIDAS"}
        </strong>

        <span>LA ESQUINA DEL PARQUÉS</span>
      </div>
    </aside>
  );
}

// =========================================================
// PÁGINA PRINCIPAL
// =========================================================

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [pageSettings, setPageSettings] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState(
    searchParams.get("search") || ""
  );

  const [currentPage, setCurrentPage] = useState(
    Math.max(1, Number(searchParams.get("page") || 1))
  );

  const [totalPages, setTotalPages] = useState(1);

  const categoryFilter = searchParams.get("category") || "";
  const searchFilter = searchParams.get("search") || "";

  const postsPerPage = useMemo(() => {
    const configured = Number(pageSettings?.posts_per_page);

    return Number.isFinite(configured) && configured > 0
      ? configured
      : 6;
  }, [pageSettings]);

  // =======================================================
  // CONFIGURACIÓN VISUAL DESDE DJANGO
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function loadSettings() {
      try {
        const response = await fetch(getApiUrl("/blog/settings/"), {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) return;

        const data = await response.json();

        if (!cancelled) {
          setPageSettings(data);
        }
      } catch (err) {
        console.error("CONFIGURACIÓN DE INFORMACIÓN:", err);
      }
    }

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  // =======================================================
  // CATEGORÍAS
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      try {
        const response = await fetch(
          getApiUrl("/blog/categories/?is_active=true"),
          { headers: { Accept: "application/json" } }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!cancelled) {
          setCategories(getResults(data));
        }
      } catch (err) {
        console.error("CATEGORÍAS:", err);

        if (!cancelled) {
          setCategories([]);
        }
      }
    }

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  // =======================================================
  // PUBLICACIONES DESTACADAS
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    async function loadPopular() {
      try {
        const response = await fetch(
          getApiUrl("/blog/articles/popular/?limit=5"),
          { headers: { Accept: "application/json" } }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!cancelled) {
          setPopular(
            getResults(data)
              .map(normalizeArticle)
              .filter(Boolean)
          );
        }
      } catch (err) {
        console.error("PUBLICACIONES DESTACADAS:", err);

        if (!cancelled) {
          setPopular([]);
        }
      }
    }

    loadPopular();

    return () => {
      cancelled = true;
    };
  }, []);

  // =======================================================
  // ARTÍCULOS, TORNEOS Y NOVEDADES
  // =======================================================

  useEffect(() => {
    const controller = new AbortController();

    async function loadArticles() {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();

        params.set("page", String(currentPage));
        params.set("page_size", String(postsPerPage));

        if (categoryFilter) {
          params.set("category_slug", categoryFilter);
        }

        if (searchFilter) {
          params.set("search", searchFilter);
        }

        const response = await fetch(
          getApiUrl(`/blog/articles/?${params.toString()}`),
          {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const results = getResults(data);

        const count = Number(data?.count ?? results.length);

        setArticles(
          results.map(normalizeArticle).filter(Boolean)
        );

        setTotalPages(
          Math.max(1, count > 0 ? Math.ceil(count / postsPerPage) : 1)
        );
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("CONTENIDO DE LA COMUNIDAD:", err);

        setArticles([]);
        setTotalPages(1);
        setError(
          "No fue posible cargar la información. Comprueba la conexión e inténtalo nuevamente."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadArticles();

    return () => controller.abort();
  }, [currentPage, categoryFilter, searchFilter, postsPerPage]);

  // =======================================================
  // SINCRONIZAR FILTROS CON LA URL
  // =======================================================

  useEffect(() => {
    setSearch(searchFilter);
  }, [searchFilter]);

  useEffect(() => {
    setCurrentPage(
      Math.max(1, Number(searchParams.get("page") || 1))
    );
  }, [searchParams]);

  // =======================================================
  // CATEGORÍA ACTUAL
  // =======================================================

  const currentCategory = categories.find(
    (category) => String(category.slug) === String(categoryFilter)
  );

  // =======================================================
  // TEXTOS DEL HERO
  // =======================================================

  const heroEyebrow =
    pageSettings?.hero_eyebrow ||
    "TORNEOS · EVENTOS · NOTICIAS · COMUNIDAD";

  const heroTitle =
    pageSettings?.hero_title || "VIVE";

  const heroHighlight =
    pageSettings?.hero_highlight || "LA ESQUINA";

  const heroText =
    pageSettings?.hero_text ||
    "Entérate de los próximos torneos, revive nuestras mejores partidas y descubre las novedades, eventos y actividades de nuestra comunidad.";

  const heroImage = getImageUrl(pageSettings?.hero_image);

  const pageTitle = currentCategory?.name
    ? currentCategory.name
    : searchFilter
      ? `RESULTADOS: ${searchFilter}`
      : pageSettings?.latest_title || "LO QUE PASA EN LA ESQUINA";

  // =======================================================
  // PAGINACIÓN
  // =======================================================

  function goToPage(page) {
    const safePage = Math.max(1, Math.min(page, totalPages));
    const params = new URLSearchParams(searchParams);

    params.set("page", String(safePage));
    setSearchParams(params);

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function clearFilters() {
    setSearch("");
    setSearchParams({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleHome() {
    setSearch("");
    setSearchParams({});
  }

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    return Array.from(
      { length: end - start + 1 },
      (_, index) => start + index
    );
  }, [currentPage, totalPages]);

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div
      className="blog-page"
      style={{ "--blog-bg": `url("${blogBg}")` }}
    >
      <Navbar />

      <main>
        {/* HERO PRINCIPAL */}

        <section
          className="blog-hero"
          style={{ backgroundImage: `url("${heroImage}")` }}
        >
          <div className="blog-hero-overlay">
            <Link to="/" className="blog-back-link">
              <ChevronLeft size={17} />
              Volver al inicio
            </Link>

            <div className="blog-hero-content">
              <div className="blog-hero-badge">
                <Trophy size={17} />
                LA COMUNIDAD SE ENCUENTRA AQUÍ
              </div>

              <p className="eyebrow">{heroEyebrow}</p>

              <h1>
                {heroTitle}
                <em>{heroHighlight}</em>
              </h1>

              <p className="hero-copy">{heroText}</p>

              <div className="blog-hero-actions">
                <a href="#actualidad" className="blog-primary-action">
                  EXPLORAR NOVEDADES
                  <ArrowRight size={17} />
                </a>

                <Link to="/sedes" className="blog-secondary-action">
                  <CalendarDays size={17} />
                  CONOCE LA ESQUINA
                </Link>
              </div>

              <div className="blog-hero-stats">
                <span>
                  <Trophy size={17} />
                  Torneos y partidas
                </span>

                <span>
                  <Newspaper size={17} />
                  Noticias y novedades
                </span>

                <span>
                  <Sparkles size={17} />
                  Comunidad y diversión
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ACCESOS RÁPIDOS */}

        <section className="blog-info-strip" aria-label="Información destacada">
          <InfoCard
            icon={Trophy}
            eyebrow="COMPITE"
            title="Torneos y campeonatos"
            text="Consulta las publicaciones sobre competencias, encuentros y partidas especiales."
            link="/blog?category=torneos"
            linkText="Ver información"
          />

          <InfoCard
            icon={CalendarDays}
            eyebrow="PARTICIPA"
            title="Eventos y actividades"
            text="Descubre las actividades y encuentros que hacen especial a nuestra comunidad."
            link="/blog?category=eventos"
            linkText="Explorar eventos"
          />

          <InfoCard
            icon={Bell}
            eyebrow="MANTENTE AL DÍA"
            title="Noticias de la casa"
            text="Entérate de los anuncios, novedades, promociones y próximos planes."
            link="/blog"
            linkText="Ver novedades"
          />
        </section>

        {/* CONTENIDO PRINCIPAL */}

        <div className="blog-layout" id="actualidad">
          <section className="blog-content">
            <div className="section-heading">
              <div>
                <span className="blog-section-eyebrow">
                  LA ESQUINA DEL PARQUÉS · ACTUALIDAD
                </span>

                <h2>
                  {pageTitle}
                </h2>

                <p>
                  Torneos, historias, anuncios y momentos de nuestra comunidad.
                </p>
              </div>

              <Link to="/blog" onClick={handleHome}>
                VER TODO
                <ArrowRight size={16} />
              </Link>
            </div>

            {/* ERROR */}

            {error && (
              <div className="blog-error" role="alert">
                <span className="state-label">
                  LA ESQUINA DEL PARQUÉS · INFORMACIÓN
                </span>

                <h3>
                  NO PUDIMOS CARGAR
                  <br />
                  <em>LAS NOVEDADES.</em>
                </h3>

                <p>{error}</p>

                <button
                  type="button"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw size={16} />
                  REINTENTAR
                </button>
              </div>
            )}

            {/* CARGA */}

            {loading && !error && (
              <div className="post-grid">
                {Array.from({ length: postsPerPage }).map((_, index) => (
                  <article className="post-card post-card-loading" key={`loading-${index}`}>
                    <div className="post-image">
                      <img src={blogBg} alt="" />
                    </div>

                    <div className="post-body">
                      <small>
                        <LoaderCircle size={14} />
                        CARGANDO INFORMACIÓN...
                      </small>

                      <h3>Preparando las novedades</h3>

                      <p>
                        Estamos consultando las últimas publicaciones.
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* PUBLICACIONES */}

            {!loading && !error && articles.length > 0 && (
              <div className="post-grid">
                {articles.map((post) => (
                  <article
                    className="post-card"
                    key={post.slug || post.id}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="post-image"
                    >
                      <img
                        src={post.image}
                        alt={post.title || "Novedades de La Esquina del Parqués"}
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.src = blogBg;
                        }}
                      />

                      <span className="post-category-badge">
                        {post.categoryName}
                      </span>
                    </Link>

                    <div className="post-body">
                      <div className="post-meta">
                        {post.date && (
                          <small>
                            <CalendarDays size={14} />
                            {post.date}
                          </small>
                        )}

                        {post.readTime && (
                          <small>
                            <Clock3 size={14} />
                            {post.readTime}
                          </small>
                        )}
                      </div>

                      <h3>{post.title}</h3>

                      <p>{post.excerpt}</p>

                      {post.authorName && (
                        <span className="post-author">
                          <UserRound size={14} />
                          {post.authorName}
                        </span>
                      )}

                      <Link
                        to={`/blog/${post.slug}`}
                        className="read-more"
                      >
                        LEER INFORMACIÓN
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* SIN RESULTADOS */}

            {!loading && !error && articles.length === 0 && (
              <div className="blog-empty">
                <span className="state-label">
                  LA ESQUINA DEL PARQUÉS · COMUNIDAD
                </span>

                <div className="blog-empty-icon">
                  {categoryFilter || searchFilter ? (
                    <Search size={36} />
                  ) : (
                    <Trophy size={38} />
                  )}
                </div>

                {categoryFilter || searchFilter ? (
                  <>
                    <h3>
                      NO ENCONTRAMOS
                      <br />
                      <em>RESULTADOS.</em>
                    </h3>

                    <p>
                      No encontramos publicaciones para esta búsqueda.
                      Prueba con otro término o consulta todas las novedades.
                    </p>
                  </>
                ) : (
                  <>
                    <h3>
                      PRONTO HABRÁ
                      <br />
                      <em>NUEVAS HISTORIAS.</em>
                    </h3>

                    <p>
                      Estamos preparando información sobre torneos,
                      eventos, actividades y novedades de La Esquina
                      del Parqués. Vuelve pronto para descubrir lo que viene.
                    </p>
                  </>
                )}

                <button type="button" onClick={clearFilters}>
                  <RefreshCw size={16} />
                  VER TODAS LAS NOVEDADES
                </button>
              </div>
            )}

            {/* PAGINACIÓN */}

            {!loading && !error && articles.length > 0 && totalPages > 1 && (
              <nav className="pagination" aria-label="Paginación de publicaciones">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => goToPage(currentPage - 1)}
                  aria-label="Página anterior"
                >
                  <ChevronLeft size={19} />
                </button>

                {pageNumbers[0] > 1 && (
                  <>
                    <button type="button" onClick={() => goToPage(1)}>
                      1
                    </button>

                    {pageNumbers[0] > 2 && <span>…</span>}
                  </>
                )}

                {pageNumbers.map((page) => (
                  <button
                    type="button"
                    key={page}
                    className={currentPage === page ? "active" : ""}
                    onClick={() => goToPage(page)}
                    aria-current={currentPage === page ? "page" : undefined}
                  >
                    {page}
                  </button>
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <span>…</span>
                    )}

                    <button
                      type="button"
                      onClick={() => goToPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                  aria-label="Página siguiente"
                >
                  <ChevronRight size={19} />
                </button>
              </nav>
            )}
          </section>

          {/* BARRA LATERAL */}

          <Sidebar
            categories={categories}
            popular={popular}
            search={search}
            setSearch={setSearch}
            pageSettings={pageSettings}
            categoryFilter={categoryFilter}
          />
        </div>
      </main>

      {/* FOOTER ORIGINAL IMPORTADO */}

      <Footer />
    </div>
  );
}
